/**
 * Deterministic, tolerant display parsers for the per-type rendered views
 * (ROADMAP "Rendered views"). Everything here is READ-SIDE ONLY: a parser
 * either recognizes enough shape to render a nicer view or returns `null`, and
 * the caller falls back to the raw `<Markdown>` render. Nothing validates,
 * nothing rejects, nothing round-trips — any text stays a valid document.
 *
 * The two write helpers (`toggleItemLine`, `appendItemLine`) are text edits,
 * not structured write-back: they rebuild the FULL blob by flipping or
 * appending one source line, and the caller sends that blob through the
 * existing `saveDocumentText(path, content, expectedVersion)` compare-and-swap.
 *
 * Front-matter handling mirrors the backend's light semantics
 * (`backend/src/utils/frontMatter.ts`): an optional leading `---` fence with
 * `key: value` lines, stripped for display only.
 */

/** One `<key>: <value>` front-matter line (same shape the backend reads). */
const FRONT_MATTER_LINE = /^([A-Za-z][A-Za-z0-9]*):\s*(.*)$/;

/** A front-matter `---` fence line (tolerating a trailing CR). */
function isFence(line: string): boolean {
  return line === '---' || line === '---\r';
}

/** A fenced-code delimiter line (``` or ~~~); items/headings inside are content. */
const CODE_FENCE_LINE = /^\s{0,3}(`{3,}|~{3,})/;

/** A Markdown ATX heading line, capturing its text. */
const HEADING_LINE = /^(#{1,6})\s+(.*\S)\s*$/;

/** A GFM task-list line: `- [ ] thing` / `* [x] thing`, capturing state + text. */
const TASK_LINE = /^(\s*[-*+]\s+\[)([ xX])(\]\s+)(.*)$/;

interface FrontMatterSplit {
  meta: Map<string, string>;
  /** Index of the first body line in the ORIGINAL content's line array. */
  bodyStart: number;
  /** All original lines (body = lines.slice(bodyStart)). */
  lines: string[];
}

/**
 * Splits a document into its optional leading front-matter map and body. No
 * leading `---` fence (or an unclosed one) means no front-matter — the whole
 * document is body. Line indices stay relative to the ORIGINAL content so
 * write helpers can address exact source lines.
 */
function splitFrontMatter(content: string): FrontMatterSplit {
  const lines = content.split('\n');
  if (!isFence(lines[0])) {
    return { meta: new Map(), bodyStart: 0, lines };
  }
  const closing = lines.findIndex((line, index) => index > 0 && isFence(line));
  if (closing === -1) {
    return { meta: new Map(), bodyStart: 0, lines };
  }
  const meta = new Map<string, string>();
  for (const line of lines.slice(1, closing)) {
    const match = FRONT_MATTER_LINE.exec(line.replace(/\r$/, ''));
    if (match) meta.set(match[1], match[2]);
  }
  return { meta, bodyStart: closing + 1, lines };
}

/** Unwraps a double-quoted scalar back to its string; plain scalars pass through. */
function unquoteScalar(raw: string): string {
  const value = raw.trim();
  if (value.length >= 2 && value.startsWith('"') && value.endsWith('"')) {
    try {
      const parsed: unknown = JSON.parse(value);
      if (typeof parsed === 'string') return parsed;
    } catch {
      // Not valid JSON — treat as a plain scalar.
    }
  }
  return value;
}

/** The last path segment without its extension, e.g. "lists/groceries.md" → "groceries". */
function baseName(path: string): string {
  const segment = path.split('/').pop() ?? path;
  return segment.replace(/\.md$/i, '');
}

/**
 * The filename stem, exactly as on disk (`journal/2026/07/2026-07-13.md` →
 * `2026-07-13`, `knowledge/pages/Memex.md` → `Memex`). This IS the document's
 * display name everywhere — what you see in Alma matches what you see in
 * Obsidian or `ls`.
 */
export function fileStem(path: string): string {
  return baseName(path);
}

/**
 * A humanized reading of a filename stem for consumer surfaces:
 * `move-house` → `Move house`, `books_to_read` → `Books to read`. A stem that
 * already carries its own casing (`Memex`, `APIs`) is left exactly as written —
 * only all-lowercase names get sentence-cased.
 */
export function humanizeStem(stem: string): string {
  const words = stem.replace(/[-_]+/g, ' ').trim();
  if (!words) return stem;
  if (words === words.toLowerCase()) return words.charAt(0).toUpperCase() + words.slice(1);
  return words;
}

/**
 * The display title for a document on consumer surfaces (Home, Entries,
 * Lists, search, the document page h1) — design review B1. Journal days read
 * as friendly local dates; otherwise the backend-derived title (front-matter
 * `title` → first heading) wins when it says more than the raw stem; else the
 * humanized stem. The Documents index deliberately keeps raw stems (it reads
 * like the file tree); everything else shows the user's words, not the
 * machine's. Display-only — the stored path and text never change.
 */
export function displayTitle(
  path: string,
  backendTitle?: string | null,
  style: 'long' | 'short' = 'short'
): string {
  const day = journalDayFromPath(path);
  if (day) return friendlyDay(day, style);
  const stem = fileStem(path);
  const derived = backendTitle?.trim();
  if (derived && derived !== stem) return derived;
  return humanizeStem(stem);
}

/**
 * The title the DOCUMENT PAGE h1 shows, derived from the path and the loaded
 * content: journal days read as friendly local dates, a front-matter `title`
 * wins next, and everything else gets the humanized filename stem. Null
 * content (still loading / missing document) falls back to the stem-based
 * reading so the page always has a name.
 */
export function documentTitle(path: string, content: string | null): string {
  const day = journalDayFromPath(path);
  if (day) return friendlyDay(day, 'long');
  if (content !== null) {
    const { meta } = splitFrontMatter(content);
    const fm = unquoteScalar(meta.get('title') ?? '');
    if (fm) return fm;
  }
  return humanizeStem(fileStem(path));
}

/** True when a heading's text matches any drop candidate (case-insensitive). */
function matchesDropHeading(headingText: string, dropHeading: string | string[]): boolean {
  const candidates = Array.isArray(dropHeading) ? dropHeading : [dropHeading];
  const lower = headingText.toLowerCase();
  return candidates.some((candidate) => candidate.toLowerCase() === lower);
}

/**
 * The document body with any leading front-matter fence stripped, for display.
 * When `dropHeading` is given and the body OPENS with a heading matching one of
 * the candidates (the display title, the filename stem), the heading line is
 * dropped too — the page already shows it as the title, and repeating it is
 * the "two titles with a gap" layout smell. The stored text is untouched —
 * this only decides what the renderer shows.
 */
export function displayBody(content: string, dropHeading?: string | string[]): string {
  const { bodyStart, lines } = splitFrontMatter(content);
  const body = lines.slice(bodyStart);
  if (dropHeading) {
    const firstContent = body.findIndex((line) => line.trim().length > 0);
    if (firstContent !== -1) {
      const heading = HEADING_LINE.exec(body[firstContent]);
      // Case-insensitive: a body heading repeating the filename stem ("# Ideas"
      // in ideas.md) folds regardless of casing.
      if (heading && matchesDropHeading(heading[2], dropHeading)) {
        body.splice(firstContent, 1);
      }
    }
  }
  return body.join('\n').replace(/^\n+/, '');
}

/**
 * Conventional folder → type fallback (`docs/markdown-schema.md`), used when a
 * document carries no front-matter `type`. Space-prefixed unified paths
 * (`family/lists/groceries.md`) match too, and a mounted wiki's NESTED,
 * day-named journal files (`journal/2026/2026-07-05.md`) count as journal —
 * but only day-named ones, so `journal/2026/notes.md` stays untyped rather
 * than misparsing. Unconventional paths are just untyped — they render
 * generically.
 */
export function typeFromPath(path: string): string | null {
  if (/(^|\/)journal\/[^/]+\.md$/i.test(path)) return 'journal';
  if (/(^|\/)journal\/.+\/\d{4}-\d{2}-\d{2}\.md$/i.test(path)) return 'journal';
  if (/(^|\/)lists\/[^/]+\.md$/i.test(path)) return 'list';
  if (/(^|\/)notes\/[^/]+\.md$/i.test(path)) return 'note';
  return null;
}

/**
 * The display type driving the per-type view: front-matter `type` first
 * (mirroring the backend's `peekDocumentType`), else the conventional folder,
 * else null.
 */
export function docViewType(path: string, content: string): string | null {
  const { meta } = splitFrontMatter(content);
  const fm = unquoteScalar(meta.get('type') ?? '');
  return fm.toLowerCase() || typeFromPath(path);
}

// ---------------------------------------------------------------------------
// List view
// ---------------------------------------------------------------------------

/** One interactive checklist item, addressed by its EXACT source line. */
export interface ListViewItem {
  /** Line index in the original content (the line a toggle rewrites). */
  lineIndex: number;
  checked: boolean;
  /** The item's text after the checkbox marker. */
  text: string;
}

/** The list body in order: item runs interleaved with plain Markdown chunks. */
export type ListViewBlock =
  | { kind: 'items'; items: ListViewItem[] }
  | { kind: 'markdown'; text: string };

export interface ListView {
  /** Every item, in document order (the flattened `items` blocks). */
  items: ListViewItem[];
  /** Display blocks in document order, front-matter excluded. */
  blocks: ListViewBlock[];
}

/**
 * Tolerant list parse: task-list lines (outside fenced code) become interactive
 * items; every other body line is preserved as Markdown between them. Returns
 * `null` — meaning "render raw" — when the document has no task lines AND its
 * body is more than blank lines/headings (i.e. it isn't list-shaped at all).
 * An empty or heading-only document parses as an empty list, so a brand-new
 * list still gets the add-item input.
 *
 * `dropHeading`: when the body OPENS with a heading matching one of the given
 * candidates, the heading is omitted from the display blocks (the page title
 * already shows it). Item line indices always stay relative to the original
 * content.
 */
export function parseListView(content: string, dropHeading?: string | string[]): ListView | null {
  const { bodyStart, lines } = splitFrontMatter(content);
  const items: ListViewItem[] = [];
  const blocks: ListViewBlock[] = [];
  let markdownRun: string[] = [];
  let onlyHeadingsAndBlanks = true;
  let inFence = false;
  let seenContent = false;

  const flushMarkdown = () => {
    const text = markdownRun.join('\n').replace(/^\n+|\n+$/g, '');
    if (text) blocks.push({ kind: 'markdown', text });
    markdownRun = [];
  };

  for (let i = bodyStart; i < lines.length; i += 1) {
    const line = lines[i];
    if (dropHeading && !seenContent && line.trim().length > 0) {
      seenContent = true;
      const heading = HEADING_LINE.exec(line);
      if (heading && matchesDropHeading(heading[2], dropHeading)) continue;
    }
    if (CODE_FENCE_LINE.test(line)) inFence = !inFence;
    const task = inFence || CODE_FENCE_LINE.test(line) ? null : TASK_LINE.exec(line);
    if (task) {
      flushMarkdown();
      const item: ListViewItem = {
        lineIndex: i,
        checked: task[2] !== ' ',
        text: task[4].trim(),
      };
      const last = blocks[blocks.length - 1];
      if (last && last.kind === 'items') last.items.push(item);
      else blocks.push({ kind: 'items', items: [item] });
      items.push(item);
    } else {
      if (line.trim().length > 0 && !HEADING_LINE.test(line)) onlyHeadingsAndBlanks = false;
      markdownRun.push(line);
    }
  }
  flushMarkdown();

  if (items.length === 0 && !onlyHeadingsAndBlanks) return null;
  return { items, blocks };
}

/**
 * Flips `- [ ]` ↔ `- [x]` on the EXACT source line and returns the full new
 * blob, or `null` when that line no longer looks like a task item (a stale
 * view — the caller should reload instead of writing).
 */
export function toggleItemLine(content: string, lineIndex: number): string | null {
  const lines = content.split('\n');
  const line = lines[lineIndex];
  if (line === undefined) return null;
  const match = TASK_LINE.exec(line);
  if (!match) return null;
  const flipped = match[2] === ' ' ? 'x' : ' ';
  lines[lineIndex] = `${match[1]}${flipped}${match[3]}${match[4]}`;
  return lines.join('\n');
}

/**
 * Appends a `- [ ] <text>` item line to the blob and returns the full new
 * content. Everything already in the document is preserved byte-for-byte.
 */
export function appendItemLine(content: string, text: string): string {
  const item = `- [ ] ${text.trim()}`;
  if (content.length === 0) return `${item}\n`;
  return content.endsWith('\n') ? `${content}${item}\n` : `${content}\n${item}\n`;
}

// ---------------------------------------------------------------------------
// Journal view
// ---------------------------------------------------------------------------

/** One journal moment: a bullet line, optionally led by a time and an emoji. */
export interface JournalMoment {
  /** Leading `HH:MM` when the line starts with one, else null. */
  time: string | null;
  /** Leading emoji (after any time), else null. */
  emoji: string | null;
  /** The moment's remaining text. */
  text: string;
  /** Indented continuation lines under the bullet, joined; null when none. */
  secondary: string | null;
  /**
   * Legacy pivot-1 category (`body|mind|work|care|joy`) folded from an old
   * `- category: …` metadata line; null for new-style moments. Drives the
   * tile color/emoji so old entries keep their character.
   */
  category: string | null;
}

export interface JournalView {
  /** The journal day from the FILENAME, as `YYYY-MM-DD`. */
  day: string;
  moments: JournalMoment[];
}

/**
 * The `YYYY-MM-DD` day a conventional journal path names
 * (`journal/2026-07-10.md`, space prefixes allowed — and a mounted wiki's
 * nested `journal/2026/2026-07-10.md`), or null for non-day paths
 * (`journal/orphan.md`) and impossible dates.
 */
export function journalDayFromPath(path: string): string | null {
  const match = /(^|\/)journal\/(?:[^/]+\/)*(\d{4})-(\d{2})-(\d{2})\.md$/i.exec(path);
  if (!match) return null;
  const [year, month, day] = [Number(match[2]), Number(match[3]), Number(match[4])];
  const date = new Date(year, month - 1, day);
  // Reject impossible dates (2026-13-40 rolls over rather than erroring).
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return `${match[2]}-${match[3]}-${match[4]}`;
}

/**
 * Resolves a `YYYY-MM-DD` journal day to "Today"/"Yesterday" or a friendly
 * date in the browser's LOCAL timezone — `long` ("July 10, 2026") for the
 * document page, `short` ("Jul 10, 2026") for index rows.
 */
export function friendlyDay(day: string, style: 'long' | 'short' = 'long'): string {
  const [year, month, dayOfMonth] = day.split('-').map(Number);
  const date = new Date(year, month - 1, dayOfMonth);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === now.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return date.toLocaleDateString(
    undefined,
    style === 'long'
      ? { month: 'long', day: 'numeric', year: 'numeric' }
      : { month: 'short', day: 'numeric', year: 'numeric' }
  );
}

/**
 * The tree prefix ahead of a path's `journal/` folder — `''` for the personal
 * tree (`journal/…`), `family/` for a space (`family/journal/…`) — or null for
 * paths outside any journal folder. Neighbor lookups compare prefixes so a
 * space's day never becomes the personal day's neighbor.
 */
function journalTreePrefix(path: string): string | null {
  const match = /^(|.*?\/)journal\//i.exec(path);
  return match ? match[1] : null;
}

export interface AdjacentJournalDays {
  /** The closest OLDER day's document path in the same tree, or null. */
  previous: string | null;
  /** The closest NEWER day's document path in the same tree, or null. */
  next: string | null;
}

/**
 * The nearest existing journal-day documents around `path`, chosen from
 * `allPaths` and restricted to the SAME tree (matching prefix before
 * `journal/`). Days navigate by what EXISTS — gaps are skipped rather than
 * landing on empty not-yet-written days. Non-day paths get no neighbors.
 */
export function adjacentJournalDays(path: string, allPaths: string[]): AdjacentJournalDays {
  const day = journalDayFromPath(path);
  const prefix = journalTreePrefix(path);
  if (!day || prefix === null) return { previous: null, next: null };

  let previous: { day: string; path: string } | null = null;
  let next: { day: string; path: string } | null = null;
  for (const candidate of allPaths) {
    if (candidate === path || journalTreePrefix(candidate) !== prefix) continue;
    const candidateDay = journalDayFromPath(candidate);
    if (!candidateDay || candidateDay === day) continue;
    if (candidateDay < day) {
      if (!previous || candidateDay > previous.day)
        previous = { day: candidateDay, path: candidate };
    } else if (!next || candidateDay < next.day) {
      next = { day: candidateDay, path: candidate };
    }
  }
  return { previous: previous?.path ?? null, next: next?.path ?? null };
}

/** A top-level bullet line, capturing the moment text. */
const MOMENT_LINE = /^\s{0,3}[-*+]\s+(.*\S)\s*$/;
/** A leading `HH:MM` time on a moment. */
const MOMENT_TIME = /^(\d{1,2}:\d{2})\s+(.*)$/;
/**
 * A leading emoji on a moment: one pictographic base plus any variation
 * selectors, skin-tone modifiers, or ZWJ-joined continuations (🏃‍♀️).
 */
const MOMENT_EMOJI =
  /^(\p{Extended_Pictographic}(?:\uFE0F|\p{Emoji_Modifier}|\u200D\p{Extended_Pictographic})*)\s*(.*)$/u;

/** Splits one bullet's text into its optional time, optional emoji, and rest. */
function parseMomentText(raw: string): Pick<JournalMoment, 'time' | 'emoji' | 'text'> {
  let rest = raw.trim();
  let time: string | null = null;
  const timeMatch = MOMENT_TIME.exec(rest);
  if (timeMatch) {
    time = timeMatch[1];
    rest = timeMatch[2];
  }
  let emoji: string | null = null;
  const emojiMatch = MOMENT_EMOJI.exec(rest);
  if (emojiMatch) {
    emoji = emojiMatch[1];
    rest = emojiMatch[2];
  }
  return { time, emoji, text: rest.trim() || raw.trim() };
}

/**
 * A pivot-1 write-spine metadata bullet (`- key: value`, unindented, closed
 * key set — see the retired `serializeMoment` in git history). These lines are
 * machine bookkeeping, never user prose, so the journal view folds them into
 * their `## heading` moment instead of rendering each as a moment of its own.
 */
const LEGACY_META_LINE =
  /^- (id|kind|label|category|activityType|energyImpact|occurredAt|startedAt|endedAt|circles|createdAt): (.*)$/;

/** Legacy pivot-1 category enum; anything else on `- category:` is ignored. */
const LEGACY_CATEGORIES = new Set(['body', 'mind', 'work', 'care', 'joy']);

/** Local `HH:MM` for a legacy ISO timestamp, or null when unparseable. */
function localTimeFromIso(iso: string): string | null {
  const date = new Date(iso.trim());
  if (Number.isNaN(date.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** One legacy `## heading` moment being folded from its metadata bullets. */
interface LegacyBlock {
  text: string;
  meta: Map<string, string>;
  /** Freeform notes prose following the metadata bullets. */
  notes: string[];
  /** True once at least one metadata bullet confirmed the legacy shape. */
  confirmed: boolean;
}

/** Finalizes a confirmed legacy block into one display moment. */
function legacyMoment(block: LegacyBlock): JournalMoment {
  const occurred = block.meta.get('occurredAt') ?? block.meta.get('startedAt');
  const category = (block.meta.get('category') ?? '').trim().toLowerCase();
  const notes = block.notes.join('\n').trim();
  return {
    time: occurred ? localTimeFromIso(occurred) : null,
    emoji: null,
    text: block.text,
    secondary: notes || null,
    category: LEGACY_CATEGORIES.has(category) ? category : null,
  };
}

/**
 * Tolerant journal parse for the timeline view. Recognizes a conventional
 * `journal/YYYY-MM-DD.md` document whose body is bullet moment lines —
 * optionally led by `HH:MM` and an emoji — with blank lines and headings
 * tolerated, and indented continuation lines folded into the moment above.
 *
 * Legacy pivot-1 day files are folded rather than rendered raw: a `## heading`
 * followed by `- key: value` write-spine metadata bullets becomes ONE moment
 * (heading as the text, local time from `occurredAt`/`startedAt`, the old
 * category kept for the tile), with any trailing notes prose as its secondary
 * line. Display-only — the stored text is never rewritten.
 *
 * Anything else (freeform prose outside a legacy block, fenced code, a non-day
 * filename, no moments at all) returns `null` and renders as raw Markdown.
 */
export function parseJournalView(path: string, content: string): JournalView | null {
  const day = journalDayFromPath(path);
  if (!day) return null;

  const { bodyStart, lines } = splitFrontMatter(content);
  const moments: JournalMoment[] = [];
  let secondary: string[] = [];
  let legacy: LegacyBlock | null = null;

  const attachSecondary = () => {
    if (secondary.length === 0) return;
    const last = moments[moments.length - 1];
    if (last) last.secondary = secondary.join('\n');
    secondary = [];
  };

  // A `## heading` opens a candidate legacy block; it becomes a moment only
  // once a metadata bullet confirms the old shape (a bare heading stays a
  // tolerated section label, exactly as before).
  const closeLegacy = () => {
    if (legacy?.confirmed) moments.push(legacyMoment(legacy));
    legacy = null;
  };

  for (let i = bodyStart; i < lines.length; i += 1) {
    const line = lines[i];
    if (line.trim().length === 0) continue;
    if (CODE_FENCE_LINE.test(line)) return null;

    const heading = HEADING_LINE.exec(line);
    if (heading) {
      attachSecondary();
      closeLegacy();
      legacy = { text: heading[2], meta: new Map(), notes: [], confirmed: false };
      continue;
    }

    const legacyMeta = legacy ? LEGACY_META_LINE.exec(line) : null;
    if (legacy && legacyMeta && legacy.notes.length === 0) {
      legacy.confirmed = true;
      legacy.meta.set(legacyMeta[1], legacyMeta[2]);
      continue;
    }

    const moment = MOMENT_LINE.exec(line);
    if (moment) {
      attachSecondary();
      closeLegacy();
      moments.push({ ...parseMomentText(moment[1]), secondary: null, category: null });
      continue;
    }

    // Notes prose inside a confirmed legacy block folds into that moment.
    if (legacy?.confirmed) {
      legacy.notes.push(line.trim());
      continue;
    }

    // Indented continuation under a bullet folds into that moment.
    if (/^\s{2,}\S/.test(line) && moments.length > 0) {
      secondary.push(line.trim());
      continue;
    }

    // Top-level prose: not moment-shaped — render raw instead.
    return null;
  }
  attachSecondary();
  closeLegacy();

  if (moments.length === 0) return null;
  return { day, moments };
}

// ---------------------------------------------------------------------------
// Note view
// ---------------------------------------------------------------------------

export interface NoteView {
  /** Front-matter `title` → first heading → file base name. */
  title: string;
  /** The prose body: front-matter stripped, title-repeating heading dropped. */
  body: string;
}

/**
 * Body size beyond which the collapsed `max-h-40` window plausibly cuts
 * content off, so the collapsed note view (with its "Read more" toggle) is
 * worth showing. Deterministic — jsdom can't measure real heights.
 */
const NOTE_COLLAPSE_CHAR_THRESHOLD = 280;
const NOTE_COLLAPSE_LINE_THRESHOLD = 6;

/**
 * Note view parse: derives the display title (mirroring the backend's
 * `deriveTitle` precedence) and the prose body with a title-repeating opening
 * heading dropped. Returns `null` when there is nothing to collapse — a short
 * or empty note just renders generically.
 */
export function parseNoteView(path: string, content: string): NoteView | null {
  const { meta, bodyStart, lines } = splitFrontMatter(content);

  let title = unquoteScalar(meta.get('title') ?? '');
  const bodyLines = lines.slice(bodyStart);
  if (!title) {
    for (const line of bodyLines) {
      const heading = HEADING_LINE.exec(line);
      if (heading) {
        title = heading[2];
        break;
      }
    }
  }
  if (!title) title = baseName(path);

  // Drop the opening heading only when it repeats the page title (front-matter
  // title, humanized stem, or the raw stem) — a heading that says something
  // different stays visible as content.
  const firstContent = bodyLines.findIndex((line) => line.trim().length > 0);
  if (firstContent !== -1) {
    const heading = HEADING_LINE.exec(bodyLines[firstContent]);
    const candidates = [title, baseName(path), humanizeStem(baseName(path))];
    if (heading && matchesDropHeading(heading[2], candidates)) {
      bodyLines.splice(firstContent, 1);
    }
  }
  const body = bodyLines.join('\n').replace(/^\n+|\n+$/g, '');

  const collapsible =
    body.length > NOTE_COLLAPSE_CHAR_THRESHOLD ||
    body.split('\n').length > NOTE_COLLAPSE_LINE_THRESHOLD;
  if (!body || !collapsible) return null;

  return { title, body };
}

/**
 * A one-line plain-text preview for an index row: the first body line that is
 * neither blank nor a heading, with basic Markdown markers stripped. Null when
 * the document has no such line.
 */
export function notePreview(content: string): string | null {
  const { bodyStart, lines } = splitFrontMatter(content);
  for (let i = bodyStart; i < lines.length; i += 1) {
    const line = lines[i].trim();
    if (!line || HEADING_LINE.test(lines[i]) || CODE_FENCE_LINE.test(lines[i])) continue;
    return line
      .replace(/^[-*+]\s+(\[[ xX]\]\s+)?/, '')
      .replace(/^>\s*/, '')
      .replace(/[*_`~]/g, '');
  }
  return null;
}

/** Checklist tallies for the index's "N of M tended" progress line. */
export interface ChecklistCount {
  total: number;
  done: number;
}

/** Counts GFM task-list items (outside fenced code) across the document. */
export function countChecklist(content: string): ChecklistCount {
  const view = parseListView(content);
  const items = view?.items ?? [];
  return { total: items.length, done: items.filter((item) => item.checked).length };
}
